import * as Yup from 'yup';
import CircularProgress from '@material-ui/core/CircularProgress';
import Auth from '@aws-amplify/auth';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { parseFullName } from 'parse-full-name';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { rejectUnlessValue, retryPromise } from '../../lib/promises';
import routes from '../../routes';
import { useAuth, User } from '../Authentication';
import { useEngineer } from '../EngineerProvider';
import LoadingPage from '../Loading/Page';
import BasicProfileDisplay from '../profile/basic';
import useBasicProfileStorage from '../profile/basic/useBasicProfileStorage';
import { useUTMParams } from '../UTMParamsProcessor';
import { createEngineer } from '../../models/engineer';

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sideBySide: {
    flexDirection: 'column-reverse',
    flex: 1,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    '& > *': {
      flex: 'initial',
      margin: theme.spacing(2),
    },
    '@media (min-width: 760px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& > *': {
        flex: 1,
      },
    },
  },
  form: {
    '& > *': {
      display: 'block',
      marginTop: theme.spacing(2),
    },
    '& > :last-child': {
      marginTop: theme.spacing(3),
    },
  },
}));

const schema = Yup.object().shape({
  firstName: Yup.string().required('We need to know your name').min(2, 'I bet you have more characters in your name'),
  lastName: Yup.string().required('We need to know your last name').min(2, 'I bet you have more characters in your last name'),
  email: Yup.string().required('How else will we communicate with you?').email('Are you sure this is right?'),
});

export default function FinishSignup() {
  const classes = useStyles();
  const history = useHistory();
  const utmParams = useUTMParams();
  const { basicProfile, fetchingBasicProfile } = useBasicProfileStorage();
  const { user, cognitoUser, refreshUser } = useAuth();
  const { reload: reloadEngineer } = useEngineer();
  const initialUser = useMemo(
    () => {
      const parsedName = parseFullName(user!.name || '');
      return {
        ...user!,
        firstName: parsedName.first,
        lastName: parsedName.last,
      };
    },
    [user],
  );
  const handleSubmit = useCallback(
    async (userValues: User) => {
      await Auth.updateUserAttributes(cognitoUser, {
        'custom:contactEmail': userValues.email,
        'custom:firstName': userValues.firstName,
        'custom:lastName': userValues.lastName,
      });
      await refreshUser();
      await createEngineer({
        experience: basicProfile.experience,
        skillIds: basicProfile.skills.map((skill) => skill.id),
        utm: utmParams,
      });
      // reload engineer with retries until we get the engineer (or give up :( )
      try {
        await retryPromise(() => reloadEngineer().then(rejectUnlessValue));
      } catch (err) {
        // if after all the tries we still couldn't load the engineer,
        // we will still go to success page so a reload there will get you the engineer
      }
      history.push(routes.jobs.potential, { fromSignup: true });
    },
    [cognitoUser, refreshUser, basicProfile, history, reloadEngineer, utmParams],
  );
  if (!user || fetchingBasicProfile) return <LoadingPage />;
  return (
    <Box className={classes.container}>
      <Typography variant="h3" component="h1" className={classes.title}>
        Just the final step!
      </Typography>
      <Box className={classes.sideBySide}>
        <BasicProfileDisplay profile={basicProfile} user={user} />
        <Box>
          <Typography>
            These are the contact details we got from GitHub. Please confirm or update them
            and you&apos;ll be ready.
          </Typography>
          <Formik
            initialValues={{ ...initialUser }}
            onSubmit={handleSubmit}
            validationSchema={schema}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form className={classes.form}>
                <Field
                  component={TextField}
                  autoComplete="given-name"
                  name="firstName"
                  label="first name"
                />
                <Field
                  component={TextField}
                  autoComplete="family-name"
                  name="lastName"
                  label="last name"
                />
                <Field
                  component={TextField}
                  autoComplete="email"
                  name="email"
                  label="email"
                />
                <Box>
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress color="inherit" size={20} />}
                    autoFocus
                    size="large"
                  >
                    Confirm and see the jobs
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
}
